<script lang="ts">
	import captureClick from '$lib/actions/captureClick';
	import type { ButtonColor, ButtonSize, ButtonVariant } from '$lib/storyblok/types';
	import { type Snippet } from 'svelte';
	import type { HTMLAttributes } from 'svelte/elements';

	type BtnHref = string | undefined;

	interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
		variant?: ButtonVariant;
		color?: ButtonColor;
		size?: ButtonSize;
		href?: BtnHref;
		type?: BtnHref extends string ? never : HTMLButtonElement['type'];
		title?: string;
		children: Snippet;
	}

	let {
		variant = 'primary',
		color = 'green',
		size = 'medium',
		href,
		type,
		title,
		children,
		...other
	}: Props = $props();

	let element = $derived(href ? 'a' : 'button') as 'a' | 'button';
	let isBtn = $derived(element === 'button');

	const themeClasses: Record<ButtonColor, Record<ButtonVariant, { btn: string; fill: string }>> = {
		green: {
			primary: {
				btn: 'text-stone-5 bg-green-3',
				fill: 'bg-green-2'
			},
			secondary: {
				btn: 'text-green-3 border-2 border-green-3 lg:hover:text-stone-5',
				fill: 'bg-green-3'
			},
			tertiary: {
				btn: 'text-green-3',
				fill: 'bg-stone-5/10'
			}
		},
		stone: {
			primary: {
				btn: 'text-green-3 bg-stone-5 lg:hover:text-stone-5',
				fill: 'bg-green-3'
			},
			secondary: {
				btn: 'text-stone-5 border-2 border-stone-5 lg:hover:text-green-3',
				fill: 'bg-stone-5'
			},
			tertiary: {
				btn: 'text-stone-5',
				fill: 'bg-green-3/10'
			}
		},
		brown: {
			primary: {
				btn: 'text-stone-5 bg-brown-1 lg:hover:text-brown-1',
				fill: 'bg-stone-5'
			},
			secondary: {
				btn: 'text-brown-1 border-2 border-brown-1 lg:hover:text-stone-5',
				fill: 'bg-brown-1'
			},
			tertiary: {
				btn: 'text-brown-1',
				fill: 'bg-stone-5/10'
			}
		},
		'nyc-white-red': {
			primary: {
				btn: 'text-[#460E00] bg-white',
				fill: 'bg-brown-5'
			},
			secondary: {
				btn: 'text-[#460E00] bg-white',
				fill: 'bg-brown-5'
			},
			tertiary: {
				btn: 'text-[#460E00] bg-white',
				fill: 'bg-brown-5'
			}
		},
		'nyc-white-blue': {
			primary: {
				btn: 'text-[#081632] bg-white',
				fill: 'bg-brown-5'
			},
			secondary: {
				btn: 'text-[#081632] bg-white',
				fill: 'bg-brown-5'
			},
			tertiary: {
				btn: 'text-[#081632] bg-white',
				fill: 'bg-brown-5'
			}
		},
		'nyc-dark': {
			primary: {
				btn: 'text-white bg-brown-1',
				fill: 'bg-[#121212]'
			},
			secondary: {
				btn: 'text-white bg-brown-1',
				fill: 'bg-[#121212]'
			},
			tertiary: {
				btn: 'text-white bg-brown-1',
				fill: 'bg-[#121212]'
			}
		}
	};

	const sizeClasses: Record<ButtonSize, string> = {
		small: 'px-4 py-3 text-[16px]/[110%]',
		medium: 'px-6 py-4 text-[18px]/[110%]',
		large: 'px-8 py-5 text-[18px]/[110%]'
	};

	let themeClass = $derived(themeClasses[color][variant]);
	let sizeClass = $derived(sizeClasses[size]);
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<svelte:element
	this={element}
	use:captureClick
	{...other}
	type={isBtn && !type ? 'button' : type}
	class="{themeClass.btn} {sizeClass} group/btn relative block overflow-hidden rounded-full font-semibold transition-colors duration-[350ms] focus:outline-none disabled:opacity-25"
	{href}
	{title}
>
	<span class="relative z-10 select-none">
		{@render children()}
	</span>
	<span
		class="{themeClass.fill} ease-[cubic-bezier(0.24, 0.8, 0.4, 1)] pointer-events-none absolute inset-0 block translate-y-full transform-gpu transition-transform duration-[350ms] max-lg:hidden lg:group-hover/btn:translate-y-0"
	></span>
</svelte:element>
